import React, { useCallback, useState } from 'react';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import useAuthorizedUsersQuery from './queries/useAuthorizedUsers.query';
import useCreateAuthorizedUserQuery from './queries/useCreateAuthorizedUser.query';
import useDeactivateAuthorizedUserQuery from './queries/useDeactivateAuthorizedUser.query';
import useUpdateAuthorizedUserQuery from './queries/useUpdateAuthorizedUser.query';
import { AuthorizedUser, CreateAuthorizedUserParams, UpdateAuthorizedUserParams } from './authorizedUsers.types';
import AuthorizedUsers from './components';

const AuthorizedUsersContainer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AuthorizedUser | null>(null);
  const [deactivatingUserId, setDeactivatingUserId] = useState<string | null>(null);

  const { authorizedUsers, isLoading } = useAuthorizedUsersQuery();
  const { createAuthorizedUser, isPending: isCreating } = useCreateAuthorizedUserQuery();
  const { updateAuthorizedUser, isPending: isUpdating } = useUpdateAuthorizedUserQuery(editingUser?.id);
  const { deactivateAuthorizedUser, isPending: isDeactivating } = useDeactivateAuthorizedUserQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const handleOpenAddModal = useCallback(() => {
    setEditingUser(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((user: AuthorizedUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
  }, []);

  const handleDeactivate = useCallback((id: string) => {
    setDeactivatingUserId(id);
  }, []);

  const handleCreate = useCallback(async (params: CreateAuthorizedUserParams) => {
    await createAuthorizedUser(params);
    handleCloseModal();
    handleAddPermanentSnackbar({
      text: `${params.firstName} ${params.lastName} has been added`,
      state: SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
  }, [createAuthorizedUser, handleCloseModal, handleAddPermanentSnackbar]);

  const handleUpdate = useCallback(async (params: UpdateAuthorizedUserParams) => {
    await updateAuthorizedUser(params);
    handleCloseModal();
    handleAddPermanentSnackbar({
      text: `${params.firstName} ${params.lastName} has been edited`,
      state: SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
  }, [updateAuthorizedUser, handleCloseModal, handleAddPermanentSnackbar]);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!deactivatingUserId) return;
    const deactivatingUser = authorizedUsers.find((user) => user.id === deactivatingUserId);
    await deactivateAuthorizedUser(deactivatingUserId);
    setDeactivatingUserId(null);
    handleAddPermanentSnackbar({
      text: `${deactivatingUser?.firstName} ${deactivatingUser?.lastName} has been deactivated`,
      state: SnackbarState.negative,
      closeIcon: true,
      autoRemove: true,
    });
  }, [authorizedUsers, deactivateAuthorizedUser, deactivatingUserId, handleAddPermanentSnackbar]);

  return (
    <AuthorizedUsers
      authorizedUsers={authorizedUsers}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      editingUser={editingUser}
      deactivatingUserId={deactivatingUserId}
      isSubmitting={isCreating || isUpdating}
      isDeactivating={isDeactivating}
      onAddNew={handleOpenAddModal}
      onEdit={handleEdit}
      onDeactivate={handleDeactivate}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onConfirmDeactivate={handleConfirmDeactivate}
      onCloseModal={handleCloseModal}
      onCloseDeactivateModal={() => setDeactivatingUserId(null)}
    />
  );
};

export default AuthorizedUsersContainer;
